"use client"

import { ComponentType, ReactElement, Ref, useRef, useState } from "react"

import IconEye from "@/components/icons/eye"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupInputProps,
  InputGroupSize,
} from "./input-group"

type PasswordInputControlComponent = ComponentType<{ render: ReactElement }>

export interface PasswordInputProps extends Omit<InputGroupInputProps, "type"> {
  showPasswordToggle?: boolean
  groupClassName?: string
  size?: InputGroupSize
  control?: PasswordInputControlComponent
}

function PasswordInput({
  ref,
  showPasswordToggle = true,
  disabled,
  className,
  groupClassName,
  size = "md",
  control: Control,
  ...props
}: PasswordInputProps & { ref?: Ref<HTMLInputElement> }) {
  const [showPassword, setShowPassword] = useState(false)
  const [prevDisabled, setPrevDisabled] = useState(disabled)
  const inputRef = useRef<HTMLInputElement | null>(null)

  if (disabled !== prevDisabled) {
    if (disabled && showPassword) {
      setShowPassword(false)
    }
    setPrevDisabled(disabled)
  }

  const setInputRef = (node: HTMLInputElement | null) => {
    inputRef.current = node
    if (typeof ref === "function") {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  }

  const handleTogglePassword = () => {
    setShowPassword((current) => !current)
    if (!disabled) {
      inputRef.current?.focus()
    }
  }

  const input = (
    <InputGroupInput
      ref={setInputRef}
      type={showPassword ? "text" : "password"}
      className={className}
      disabled={disabled}
      autoCorrect="off"
      spellCheck="false"
      autoCapitalize="off"
      data-form-type="password"
      {...props}
    />
  )

  return (
    <InputGroup className={groupClassName} size={size}>
      {Control ? <Control render={input} /> : input}
      {showPasswordToggle && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            icon={IconEye}
            variant="ghost"
            disabled={disabled}
            label={showPassword ? "Hide password" : "Show password"}
            className={
              showPassword
                ? "text-foreground"
                : "text-muted hover:text-foreground"
            }
            onClick={handleTogglePassword}
          />
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}

PasswordInput.displayName = "PasswordInput"

export { PasswordInput }
